import React, { useState, useEffect } from "react";
import Select from "react-select";
import api from "../../api/api";
import ExcelExport from "../../UserControls/ExcelExport/ExcelExport";

const Calibration = ({ UserName, Ownership, AssetID }) => {
  const storedAssetId = parseInt(sessionStorage.getItem("currentAssetId")) || 0;

  const [showPopup, setShowPopup] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [ctypeOptions, setCtypeOptions] = useState([]);
  const [calibrationByOptions, setCalibrationByOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [calibrationList, setCalibrationList] = useState([]);
  const [calibrationLocationOptions, setCalibrationLocationOptions] = useState([]);
  const [calibrationGroupOptions, setCalibrationGroupOptions] = useState([]);
  const [responsibilityOptions, setResponsibilityOptions] = useState([]); // ✅ new

  const [formData, setFormData] = useState({
    calibrationGroup: null,
    ctype: null,
    intervalDays: "",
    lastDate: new Date().toISOString().split("T")[0],
    nextDate: new Date().toISOString().split("T")[0],
    status: null,
    calibrationBy: null,
    calibrationLocation: null,
    responsibility: null, // ✅ new
    responsibilityId: 0, // ✅ new hidden field
  });

  const formatForInput = (dateStr) => dateStr?.split("T")[0] ?? "";

  const resetForm = () => {
    setFormData({
      calibrationGroup: null,
      ctype: null,
      intervalDays: "",
      lastDate: new Date().toISOString().split("T")[0],
      nextDate: new Date().toISOString().split("T")[0],
      status: null,
      calibrationBy: null,
      calibrationLocation: null,
      responsibility: null,
      responsibilityId: 0,
    });
  };

  const filteredCalibrationByOptions = calibrationByOptions.filter((opt) => {
    if (formData.calibrationLocation === "Internal") {
      return opt.label === "Not Applicable";
    } else {
      return opt.label !== "Not Applicable";
    }
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      if (!UserName) return;
      try {
        const data = await api.get("GetAssetRegisterAPI", {
          User: UserName,
          Ownership: Ownership || "",
        });

        setCtypeOptions(
          (data?.Table17 || []).map((c) => ({
            value: c.CtId,
            label: c.TypeName,
          }))
        );

        setCalibrationByOptions(
          (data?.Table10 || []).map((v) => ({
            value: v.Calibrationid,
            label: v.Calibrationby,
          }))
        );

        setStatusOptions(
          (data?.Table18 || []).map((s) => ({
            value: s.Status,
            label: s.Status,
          }))
        );

        setCalibrationLocationOptions(
          (data?.Table11 || []).map((loc) => ({
            value: loc.CalibirationLoc,
            label: loc.CalibirationLoc,
          }))
        );

        setCalibrationGroupOptions(
          (data?.Table20 || []).map((cal) => ({
            value: cal.CalibrationGroup,
            label: cal.CalibrationGroup,
          }))
        );

        // ✅ Responsibility dropdown (Table8)
        setResponsibilityOptions(
          (data?.Table8 || []).map((u) => ({
            value: u.UserId,
            label: u.UserName,
          }))
        );

      } catch (err) {
        console.error("Error fetching dropdowns:", err);
      }
    };

    fetchDropdowns();
  }, [UserName, Ownership]);

  useEffect(() => {
    const fetchCalibrations = async () => {
      const id = AssetID;
      if (!id || !UserName) return;

      try {
        const data = await api.GetAssetCalibration(id, UserName);

        const list = (data?.Table || []).map((item) => ({
          id: item.AstCalId,
          assetId: id,
          ctid: item.CTID,
          CalibrationGroup: item.CalibrationGroup ?? "",
          ctype: item.CtypeName,
          intervalDays: item.IntervalDays,
          lastDate: formatForInput(item.LastCalibrationDate),
          nextDate: formatForInput(item.NextDueDate),
          status: item.Status,
          calibrationBy:
            calibrationByOptions.find((i) => i.value === item.Vid)?.label ?? item.VName ?? "",
          vid: item.Vid,
          calibrationLocation: item.CalibrationLoc ?? "",
          responsibility: item.Responsibility ?? "", // ✅ bind new field
        }));

        setCalibrationList(list);
      } catch (err) {
        console.error("Error fetching calibrations:", err);
      }
    };

    if (AssetID && calibrationByOptions.length > 0) {
      fetchCalibrations();
    }
  }, [AssetID, UserName, calibrationByOptions]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      let updated = { ...prev, [field]: value };

      if (field === "responsibility") {
        updated.responsibilityId = value || 0; // ✅ set hidden field value
      }

      if (field === "intervalDays" || field === "lastDate") {
        const days = parseInt(field === "intervalDays" ? value : prev.intervalDays);
        const last = new Date(field === "lastDate" ? value : prev.lastDate);
        if (!isNaN(days) && last instanceof Date && !isNaN(last)) {
          const next = new Date(last);
          next.setDate(last.getDate() + days);
          updated.nextDate = next.toISOString().split("T")[0];
        }
      }

      return updated;
    });
  };

  const handleSave = async () => {
    const assetIdToUse = AssetID || storedAssetId;
    if (!assetIdToUse || !UserName) {
      alert("Asset ID or User is missing!");
      return;
    }

    const payload = {
      Sp_SqlAction: editingId ? "UPDATE" : "INSERT",
      AstCalId: editingId || 0,
      AssetID: assetIdToUse,
      CTID: formData.ctype ?? 0,
      CtypeName: ctypeOptions.find((i) => i.value === formData.ctype)?.label ?? "",
      IntervalDays: parseInt(formData.intervalDays) || 0,
      LastCalibrationDate: formData.lastDate,
      NextDueDate: formData.nextDate,
      Status: formData.status?.value ?? "",
      Vid: formData.calibrationBy ?? 0,
      CalibrationLoc: formData.calibrationLocation ?? "",
      CalibrationGroup:
        calibrationGroupOptions.find((i) => i.value === formData.calibrationGroup)?.label ?? "",
      ResponsibilityId: formData.responsibilityId ?? 0, // ✅ send hidden field
      Responsibility:
        responsibilityOptions.find((i) => i.value === formData.responsibility)?.label ?? "",
      UserName: UserName,
    };

    try {
      await api.post("SaveAssetCalibrationAPI", payload);

      setEditingId(null);
      setShowPopup(false);
      resetForm();

      const updatedData = await api.GetAssetCalibration(assetIdToUse, UserName);
      const updatedList = (updatedData?.Table || []).map((item) => ({
        id: item.AstCalId,
        assetId: assetIdToUse,
        ctid: item.CTID,
        ctype: item.CtypeName,
        intervalDays: item.IntervalDays,
        lastDate: formatForInput(item.LastCalibrationDate),
        nextDate: formatForInput(item.NextDueDate),
        status: item.Status,
        calibrationBy:
          calibrationByOptions.find((i) => i.value === item.Vid)?.label ?? item.VName ?? "",
        vid: item.Vid,
        calibrationLocation: item.CalibrationLoc ?? "",
        CalibrationGroup: item.CalibrationGroup ?? "",
        responsibility: item.Responsibility ?? "", // ✅ bind new field
      }));

      setCalibrationList(updatedList);
    } catch (err) {
      console.error("Error saving calibration:", err);
      alert("Failed to save calibration. Please try again.");
    }
  };

  const handleEdit = async (record) => {
    try {
      const data = await api.EditAssetCalibration(record.assetId);
      const editList = data?.EditAssetCalibrationAPIjs?.Table || data?.Table || [];

      if (!editList || editList.length === 0) {
        alert("No data found for this asset in API response.");
        return;
      }

      const editRecord = editList.find(
        (r) => String(r.AstCalId) === String(record.id)
      );

      if (!editRecord) {
        alert("No matching calibration record found for this ID.");
        return;
      }

      const formatted = {
        calibrationGroup:
          calibrationGroupOptions.find((i) => String(i.label) === String(editRecord.CalibrationGroup))?.value ?? null,
        ctype: ctypeOptions.find((i) => String(i.value) === String(editRecord.CTID))?.value ?? null,
        intervalDays: editRecord.IntervalDays ?? "",
        lastDate: formatForInput(editRecord.LastCalibrationDate),
        nextDate: formatForInput(editRecord.NextDueDate),
        status: statusOptions.find((s) => String(s.value) === String(editRecord.Status)) ?? null,
        calibrationBy: calibrationByOptions.find((i) => String(i.value) === String(editRecord.Vid))?.value ?? null,
        calibrationLocation:
          calibrationLocationOptions.find((i) => String(i.value) === String(editRecord.CalibrationLoc))?.value ?? null,
        responsibility:
          responsibilityOptions.find((i) => String(i.value) === String(editRecord.ResponsibilityId))?.value ?? null,
        responsibilityId: editRecord.ResponsibilityId ?? 0,
      };

      setFormData(formatted);
      setEditingId(editRecord.AstCalId);
      setShowPopup(true);
    } catch (err) {
      console.error("Error fetching edit data:", err);
      alert("Failed to fetch edit data. Please try again.");
    }
  };

  const thStyle = { padding: "8px", border: "1px solid #d1d5db" };
  const tdStyle = { padding: "8px", border: "1px solid #d1d5db" };
  const editBtnStyle = {
    padding: "5px 10px",
    borderRadius: "5px",
    border: "none",
    backgroundColor: "#f59e0b",
    color: "white",
    cursor: "pointer",
  };

  return (
    <>
      <div style={{ textAlign: "center", marginTop: "15px" }}>
        <button
          onClick={() => setShowPopup(true)}
          style={{
            padding: "8px 20px",
            borderRadius: "6px",
            border: "none",
            backgroundColor: "#2563eb",
            color: "white",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          + Add Calibration
        </button>

  <ExcelExport data={calibrationList} fileName="CalibrationList" />

      </div>

      {calibrationList.length > 0 && (
        <div style={{ marginTop: "20px", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              textAlign: "left",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f3f4f6" }}>
                <th style={thStyle}>Calibration Group</th>
                <th style={thStyle}>Calibration Type</th>
                <th style={thStyle}>Last Calibration Date</th>
                <th style={thStyle}>Interval Days</th>
                <th style={thStyle}>Next Due Date</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Responsibility</th>
               
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {calibrationList.map((cal) => (
                <tr key={cal.id}>
                  <td style={tdStyle}>{cal.CalibrationGroup}</td>
                  <td style={tdStyle}>{cal.ctype}</td>
                  <td style={tdStyle}>{cal.lastDate}</td>
                  <td style={tdStyle}>{cal.intervalDays}</td>
                  <td style={tdStyle}>{cal.nextDate}</td>
                  <td style={tdStyle}>{cal.status}</td>
                  <td style={tdStyle}>{cal.responsibility}</td> {/* ✅ new */}
                 
                  <td style={tdStyle}>
                    <button onClick={() => handleEdit(cal)} style={editBtnStyle}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Popup Form */}
      {showPopup && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              padding: "25px 30px",
              width: "90%",
              maxWidth: "750px",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
              position: "relative",
              textAlign: "center",
            }}
          >
            <button
              onClick={() => {
                setShowPopup(false);
                setEditingId(null);
                resetForm();
              }}
              style={{
                position: "absolute",
                top: "10px",
                right: "15px",
                fontSize: "22px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9ca3af",
              }}
            >
              ✕
            </button>

            <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "20px" }}>
              Calibration Details
            </h2>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                alignItems: "center",
              }}
            >
              {[
                {
                  label: "Calibration Group",
                  element: (
                    <Select
                      options={calibrationGroupOptions}
                      value={
                        calibrationGroupOptions.find(
                          (i) => i.value === formData.calibrationGroup
                        ) ?? null
                      }
                      onChange={(option) =>
                        handleChange("calibrationGroup", option?.value ?? null)
                      }
                      isClearable
                    />
                  ),
                },
                {
                  label: "Calibration Type",
                  element: (
                    <Select
                      options={ctypeOptions}
                      value={ctypeOptions.find((i) => i.value === formData.ctype) ?? null}
                      onChange={(option) => handleChange("ctype", option?.value ?? null)}
                      isClearable
                    />
                  ),
                },
                {
                  label: "Last Calibration Date",
                  element: (
                    <input
                      type="date"
                      value={formData.lastDate}
                      onChange={(e) => handleChange("lastDate", e.target.value)}
                      style={{
                        width: "40%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                      }}
                    />
                  ),
                },
                {
                  label: "Interval Days",
                  element: (
                    <input
                      type="number"
                      value={formData.intervalDays}
                      onChange={(e) => handleChange("intervalDays", e.target.value)}
                      style={{
                        width: "40%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                      }}
                    />
                  ),
                },
                {
                  label: "Next Due Date",
                  element: (
                    <input
                      type="date"
                      value={formData.nextDate}
                      readOnly
                      style={{
                        width: "40%",
                        padding: "8px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                      }}
                    />
                  ),
                },
                {
                  label: "Status",
                  element: (
                    <Select
                      options={statusOptions}
                      value={formData.status ?? null}
                      onChange={(option) => handleChange("status", option ?? null)}
                      isClearable
                    />
                  ),
                },
                {
                  label: "Calibration Location",
                  element: (
                    <Select
                      options={calibrationLocationOptions}
                      value={
                        calibrationLocationOptions.find(
                          (i) => i.value === formData.calibrationLocation
                        ) ?? null
                      }
                      onChange={(option) =>
                        handleChange("calibrationLocation", option?.value ?? null)
                      }
                      isClearable
                    />
                  ),
                },
                {
                  label: "Calibration By",
                  element: (
                    <Select
                      options={filteredCalibrationByOptions}
                      value={
                        filteredCalibrationByOptions.find(
                          (i) => i.value === formData.calibrationBy
                        ) ?? null
                      }
                      onChange={(option) =>
                        handleChange("calibrationBy", option?.value ?? null)
                      }
                      isClearable
                    />
                  ),
                },
                // ✅ New Responsibility Field
                {
                  label: "Responsibility",
                  element: (
                    <>
                      <Select
                        options={responsibilityOptions}
                        value={
                          responsibilityOptions.find(
                            (i) => i.value === formData.responsibility
                          ) ?? null
                        }
                        onChange={(option) =>
                          handleChange("responsibility", option?.value ?? null)
                        }
                        isClearable
                      />
                      <input
                        type="hidden"
                        value={formData.responsibilityId || 0}
                        readOnly
                      />
                    </>
                  ),
                },
              ].map((field, idx) => (
                <div key={idx} style={{ width: "40%", textAlign: "left" }}>
                  <label
                    style={{
                      fontWeight: 600,
                      marginBottom: "4px",
                      display: "block",
                    }}
                  >
                    {field.label}
                  </label>
                  {field.element}
                </div>
              ))}

              <div style={{ textAlign: "center", marginTop: "15px" }}>
                <button
                  onClick={handleSave}
                  style={{
                    padding: "10px 35px",
                    borderRadius: "8px",
                    border: "none",
                    backgroundColor: "#3b82f6",
                    color: "#fff",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {editingId ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Calibration;



// import React, { useState, useEffect } from "react";
// import Select from "react-select";
// import api from "../../api/api";

// const Calibration = ({ UserName, Ownership, AssetID }) => {
//   const storedAssetId = parseInt(sessionStorage.getItem("currentAssetId")) || 0;

//   const [showPopup, setShowPopup] = useState(false);
//   const [editingId, setEditingId] = useState(null);

//   const [ctypeOptions, setCtypeOptions] = useState([]);
//   const [calibrationByOptions, setCalibrationByOptions] = useState([]);
//   const [statusOptions, setStatusOptions] = useState([]);
//   const [calibrationList, setCalibrationList] = useState([]);
//   const [calibrationLocationOptions, setCalibrationLocationOptions] = useState([]);
// const [calibrationGroupOptions, setCalibrationGroupOptions] = useState([]);
//   const [formData, setFormData] = useState({
//    calibrationGroup: null,
//     ctype: null,
//     intervalDays: "",
//     lastDate: new Date().toISOString().split("T")[0],
//     nextDate: new Date().toISOString().split("T")[0],
//     status: null,
//     calibrationBy: null,
//     calibrationLocation: null,
//   });

//   const formatForInput = (dateStr) => dateStr?.split("T")[0] ?? "";

//   const resetForm = () => {
//     setFormData({
//       calibrationGroup: null,
//       ctype: null,
//       intervalDays: "",
//       lastDate: new Date().toISOString().split("T")[0],
//       nextDate: new Date().toISOString().split("T")[0],
//       status: null,
//       calibrationBy: null,
//       calibrationLocation: null,
//     });
//   };

//   const filteredCalibrationByOptions = calibrationByOptions.filter((opt) => {
//     if (formData.calibrationLocation === "Internal") {
//       return opt.label === "Not Applicable";
//     } else {
//       return opt.label !== "Not Applicable";
//     }
//   });

//   useEffect(() => {
//     const fetchDropdowns = async () => {
//       if (!UserName) return;
//       try {
//         const data = await api.get("GetAssetRegisterAPI", {
//           User: UserName,
//           Ownership: Ownership || "",
//         });

//         setCtypeOptions(
//           (data?.Table17 || []).map((c) => ({
//             value: c.CtId,
//             label: c.TypeName,
//           }))
//         );

//         setCalibrationByOptions(
//           (data?.Table10 || []).map((v) => ({
//             value: v.Calibrationid,
//             label: v.Calibrationby,
//           }))
//         );

//         setStatusOptions(
//           (data?.Table18 || []).map((s) => ({
//             value: s.Status,
//             label: s.Status,
//           }))
//         );

//         setCalibrationLocationOptions(
//           (data?.Table11 || []).map((loc) => ({
//             value: loc.CalibirationLoc,
//             label: loc.CalibirationLoc,
//           }))
//         );

//         setCalibrationGroupOptions(
//   (data?.Table20 || []).map((cal) => ({
//     value: cal.CalibrationGroup,
//     label: cal.CalibrationGroup,
//   }))
// );


//       } catch (err) {
//         console.error("Error fetching dropdowns:", err);
//       }
//     };

//     fetchDropdowns();
//   }, [UserName, Ownership]);

//   useEffect(() => {
//     const fetchCalibrations = async () => {
//       const id = AssetID;
//       if (!id || !UserName) return;

//       try {
//         const data = await api.GetAssetCalibration(id, UserName);

//         const list = (data?.Table || []).map((item) => ({
//           id: item.AstCalId,
//           assetId: id,
//           ctid: item.CTID,
//          CalibrationGroup: item.CalibrationGroup ?? "",
//           ctype: item.CtypeName,
//           intervalDays: item.IntervalDays,
//           lastDate: formatForInput(item.LastCalibrationDate),
//           nextDate: formatForInput(item.NextDueDate),
//           status: item.Status,
//           calibrationBy:
//             calibrationByOptions.find((i) => i.value === item.Vid)?.label ??
//             item.VName ??
//             "",
//           vid: item.Vid,
//           calibrationLocation: item.CalibrationLoc ?? "",
//         }));

//         setCalibrationList(list);
//       } catch (err) {
//         console.error("Error fetching calibrations:", err);
//       }
//     };

//     if (AssetID && calibrationByOptions.length > 0) {
//       fetchCalibrations();
//     }
//   }, [AssetID, UserName, calibrationByOptions]);

//   const handleChange = (field, value) => {
//     setFormData((prev) => {
//       let updated = { ...prev, [field]: value };

//       if (field === "intervalDays" || field === "lastDate") {
//         const days = parseInt(field === "intervalDays" ? value : prev.intervalDays);
//         const last = new Date(field === "lastDate" ? value : prev.lastDate);
//         if (!isNaN(days) && last instanceof Date && !isNaN(last)) {
//           const next = new Date(last);
//           next.setDate(last.getDate() + days);
//           updated.nextDate = next.toISOString().split("T")[0];
//         }
//       }

//       return updated;
//     });
//   };

//   const handleSave = async () => {
//     const assetIdToUse = AssetID || storedAssetId;
//     if (!assetIdToUse || !UserName) {
//       alert("Asset ID or User is missing!");
//       return;
//     }

//     const payload = {
//       Sp_SqlAction: editingId ? "UPDATE" : "INSERT",
//       AstCalId: editingId || 0,
//       AssetID: assetIdToUse,
//       CTID: formData.ctype ?? 0,
//       CtypeName:
//         ctypeOptions.find((i) => i.value === formData.ctype)?.label ?? "",
//       IntervalDays: parseInt(formData.intervalDays) || 0,
//       LastCalibrationDate: formData.lastDate,
//       NextDueDate: formData.nextDate,
//       Status: formData.status?.value ?? "",
//       Vid: formData.calibrationBy ?? 0,
//       CalibrationLoc: formData.calibrationLocation ?? "",
//     CalibrationGroup:
//   calibrationGroupOptions.find(
//     (i) => i.value === formData.calibrationGroup
//   )?.label ?? "",
//     UserName: UserName, 
//     };

//     try {
//       await api.post("SaveAssetCalibrationAPI", payload);

//       setEditingId(null);
//       setShowPopup(false);
//       resetForm();

//       const updatedData = await api.GetAssetCalibration(assetIdToUse, UserName);
//       const updatedList = (updatedData?.Table || []).map((item) => ({
//         id: item.AstCalId,
//         assetId: assetIdToUse,
//         ctid: item.CTID,
//         ctype: item.CtypeName,
//         intervalDays: item.IntervalDays,
//         lastDate: formatForInput(item.LastCalibrationDate),
//         nextDate: formatForInput(item.NextDueDate),
//         status: item.Status,
//         calibrationBy:
//           calibrationByOptions.find((i) => i.value === item.Vid)?.label ??
//           item.VName ??
//           "",
//         vid: item.Vid,
//         calibrationLocation: item.CalibrationLoc ?? "",
//         CalibrationGroup: item.CalibrationGroup ?? "", 
//       }));

//       setCalibrationList(updatedList);
//     } catch (err) {
//       console.error("Error saving calibration:", err);
//       alert("Failed to save calibration. Please try again.");
//     }
//   };

//   const handleEdit = async (record) => {
//     try {
//       const data = await api.EditAssetCalibration(record.assetId);
//       const editList = data?.EditAssetCalibrationAPIjs?.Table || data?.Table || [];

//       if (!editList || editList.length === 0) {
//         alert("No data found for this asset in API response.");
//         return;
//       }

//       const editRecord = editList.find(
//         (r) => String(r.AstCalId) === String(record.id)
//       );

//       if (!editRecord) {
//         alert("No matching calibration record found for this ID.");
//         return;
//       }

//       const formatted = {
//          calibrationGroup:
//           calibrationGroupOptions.find(
//             (i) => String(i.label) === String(editRecord.CalibrationGroup)
//           )?.value ?? null,
//         ctype:
//           ctypeOptions.find(
//             (i) => String(i.value) === String(editRecord.CTID)
//           )?.value ?? null,
//         intervalDays: editRecord.IntervalDays ?? "",
//         lastDate: formatForInput(editRecord.LastCalibrationDate),
//         nextDate: formatForInput(editRecord.NextDueDate),
//         status:
//           statusOptions.find(
//             (s) => String(s.value) === String(editRecord.Status)
//           ) ?? null,
//         calibrationBy:
//           calibrationByOptions.find(
//             (i) => String(i.value) === String(editRecord.Vid)
//           )?.value ?? null,
//         calibrationLocation:
//           calibrationLocationOptions.find(
//             (i) => String(i.value) === String(editRecord.CalibrationLoc)
//           )?.value ?? null,
//       };

//       setFormData(formatted);
//       setEditingId(editRecord.AstCalId);
//       setShowPopup(true);
//     } catch (err) {
//       console.error("Error fetching edit data:", err);
//       alert("Failed to fetch edit data. Please try again.");
//     }
//   };

//   const thStyle = { padding: "8px", border: "1px solid #d1d5db" };
//   const tdStyle = { padding: "8px", border: "1px solid #d1d5db" };
//   const editBtnStyle = {
//     padding: "5px 10px",
//     borderRadius: "5px",
//     border: "none",
//     backgroundColor: "#f59e0b",
//     color: "white",
//     cursor: "pointer",
//   };

//   return (
//     <>
//       <div style={{ textAlign: "center", marginTop: "15px" }}>
//         <button
//           onClick={() => setShowPopup(true)}
//           style={{
//             padding: "8px 20px",
//             borderRadius: "6px",
//             border: "none",
//             backgroundColor: "#2563eb",
//             color: "white",
//             fontWeight: 600,
//             cursor: "pointer",
//           }}
//         >
//           + Add Calibration
//         </button>
//       </div>

//       {calibrationList.length > 0 && (
//         <div style={{ marginTop: "20px", overflowX: "auto" }}>
//           <table
//             style={{
//               width: "100%",
//               borderCollapse: "collapse",
//               textAlign: "left",
//               fontSize: "14px",
//             }}
//           >
//             <thead>
//               <tr style={{ backgroundColor: "#f3f4f6" }}>
//                  <th style={thStyle}>Calibration Group</th>
//                 <th style={thStyle}>Calibration Type</th>
//                 <th style={thStyle}>Last Calibration Date</th>
//                 <th style={thStyle}>Interval Days</th>
//                 <th style={thStyle}>Next Due Date</th>
//                 <th style={thStyle}>Status</th>
//                 <th style={thStyle}>Responsibility</th>
//                 <th style={thStyle}>Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {calibrationList.map((cal) => (
//                 <tr key={cal.id}>
//                    <td style={tdStyle}>{cal.CalibrationGroup}</td>
//                   <td style={tdStyle}>{cal.ctype}</td>
//                   <td style={tdStyle}>{cal.lastDate}</td>
//                   <td style={tdStyle}>{cal.intervalDays}</td>
//                   <td style={tdStyle}>{cal.nextDate}</td>
//                   <td style={tdStyle}>{cal.status}</td>
//                   <td style={tdStyle}>{cal.calibrationBy}</td>
//                   <td style={tdStyle}>
//                     <button onClick={() => handleEdit(cal)} style={editBtnStyle}>
//                       Edit
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       )}

//       {showPopup && (
//         <div
//           style={{
//             position: "fixed",
//             top: 0,
//             left: 0,
//             width: "100vw",
//             height: "100vh",
//             backgroundColor: "rgba(0,0,0,0.5)",
//             display: "flex",
//             justifyContent: "center",
//             alignItems: "center",
//             zIndex: 1000,
//             backdropFilter: "blur(4px)",
//           }}
//         >
//           <div
//             style={{
//               backgroundColor: "#fff",
//               borderRadius: "10px",
//               padding: "25px 30px",
//               width: "90%",
//               maxWidth: "750px",
//               maxHeight: "85vh",
//               overflowY: "auto",
//               boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
//               position: "relative",
//               textAlign: "center",
//             }}
//           >
//             <button
//               onClick={() => {
//                 setShowPopup(false);
//                 setEditingId(null);
//                 resetForm();
//               }}
//               style={{
//                 position: "absolute",
//                 top: "10px",
//                 right: "15px",
//                 fontSize: "22px",
//                 background: "none",
//                 border: "none",
//                 cursor: "pointer",
//                 color: "#9ca3af",
//               }}
//             >
//               ✕
//             </button>

//             <h2 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "20px" }}>
//               Calibration Details
//             </h2>

//             <div
//               style={{
//                 display: "flex",
//                 flexDirection: "column",
//                 gap: "15px",
//                 alignItems: "center",
//               }}
//             >
//               {[
//                  {
//                   label: "Calibration Group",
//                   element: (
//                     <Select
//                       options={calibrationGroupOptions}
//                       value={
//                         calibrationGroupOptions.find(
//                           (i) => i.value === formData.calibrationGroup
//                         ) ?? null
//                       }
//                       onChange={(option) =>
//                         handleChange("calibrationGroup", option?.value ?? null)
//                       }
//                       isClearable
//                     />
//                   ),
//                 },
//                 {
//                   label: "Calibration Type",
//                   element: (
//                     <Select
//                       options={ctypeOptions}
//                       value={ctypeOptions.find((i) => i.value === formData.ctype) ?? null}
//                       onChange={(option) => handleChange("ctype", option?.value ?? null)}
//                       isClearable
//                     />
//                   ),
//                 },
//                 {
//                   label: "Last Calibration Date",
//                   element: (
//                     <input
//                       type="date"
//                       value={formData.lastDate}
//                       onChange={(e) => handleChange("lastDate", e.target.value)}
//                       style={{
//                         width: "40%",
//                         padding: "8px",
//                         borderRadius: "6px",
//                         border: "1px solid #d1d5db",
//                       }}
//                     />
//                   ),
//                 },
//                 {
//                   label: "Interval Days",
//                   element: (
//                     <input
//                       type="number"
//                       value={formData.intervalDays}
//                       onChange={(e) => handleChange("intervalDays", e.target.value)}
//                       style={{
//                         width: "40%",
//                         padding: "8px",
//                         borderRadius: "6px",
//                         border: "1px solid #d1d5db",
//                       }}
//                     />
//                   ),
//                 },
//                 {
//                   label: "Next Due Date",
//                   element: (
//                     <input
//                       type="date"
//                       value={formData.nextDate}
//                       readOnly
//                       style={{
//                         width: "40%",
//                         padding: "8px",
//                         borderRadius: "6px",
//                         border: "1px solid #d1d5db",
//                       }}
//                     />
//                   ),
//                 },
//                 {
//                   label: "Status",
//                   element: (
//                     <Select
//                       options={statusOptions}
//                       value={formData.status ?? null}
//                       onChange={(option) => handleChange("status", option ?? null)}
//                       isClearable
//                     />
//                   ),
//                 },
//                 {
//                   label: "Calibration Location",
//                   element: (
//                     <Select
//                       options={calibrationLocationOptions}
//                       value={
//                         calibrationLocationOptions.find(
//                           (i) => i.value === formData.calibrationLocation
//                         ) ?? null
//                       }
//                       onChange={(option) =>
//                         handleChange("calibrationLocation", option?.value ?? null)
//                       }
//                       isClearable
//                     />
//                   ),
//                 },
//                 {
//                   label: "Calibration By",
//                   element: (
//                     <Select
//                       options={filteredCalibrationByOptions}
//                       value={
//                         filteredCalibrationByOptions.find(
//                           (i) => i.value === formData.calibrationBy
//                         ) ?? null
//                       }
//                       onChange={(option) =>
//                         handleChange("calibrationBy", option?.value ?? null)
//                       }
//                       isClearable
//                     />
//                   ),
//                 },
//               ].map((field, idx) => (
//                 <div key={idx} style={{ width: "40%", textAlign: "left" }}>
//                   <label
//                     style={{
//                       fontWeight: 600,
//                       marginBottom: "4px",
//                       display: "block",
//                     }}
//                   >
//                     {field.label}
//                   </label>
//                   {field.element}
//                 </div>
//               ))}

//               <div style={{ textAlign: "center", marginTop: "15px" }}>
//                 <button
//                   onClick={handleSave}
//                   style={{
//                     padding: "10px 35px",
//                     borderRadius: "8px",
//                     border: "none",
//                     backgroundColor: "#3b82f6",
//                     color: "#fff",
//                     fontWeight: 600,
//                     cursor: "pointer",
//                   }}
//                 >
//                   {editingId ? "Update" : "Save"}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default Calibration;

